import { Component, OnInit, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CasePipe } from '../../../../pipes/case.pipe';
import { FormatPipe } from '../../../../pipes/format.pipe';
import { BaseComponent } from '../../../base/base.component';
import { IconComponent } from '../../../core/labels/icon.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { TogglePanelComponent } from '../../../core/panels/toggle-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { TimeTrackingHeaderDsComponent } from './time-tracking-header-ds.component';
import {
  OffWorkData,
  PermanentDisabilityData,
  TempDisabilityData,
  TimeTrackingHeaderWcComponent,
} from './time-tracking-header-wc.component';
import { TimeTrackingResponse } from '../../../../models/api/view/claim/details/time-tracking/time-tracking-response';
import { ExportFormat } from '../../../core/buttons/export-button.component';
import { ClaimExportMapper } from '../../../../utilities/claim-export-mapper';
import { DatePipe } from '@angular/common';
import { ExportService } from '../../../../services/export/export.service';

@Component({
  selector: 'app-time-tracking',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    RouterModule,
    TogglePanelComponent,
    CardComponent,
    CasePipe,
    FormatPipe,
    IconComponent,
    PageHeaderComponent,
    TableComponent,
    ColumnComponent,
    TagComponent,
    TimeTrackingHeaderWcComponent,
    TimeTrackingHeaderDsComponent,
    TabPanelComponent,
    TabComponent,
  ],
  template: `
    <app-page-header label="Time tracking" />

    <app-tab-panel>
      <app-tab label="Details">
        <ng-template #content>
          <div class="tw-pt-8"></div>

          @if (isWc) {
            <app-time-tracking-header-wc
              [ttd]="wcHeaderData.ttd"
              [tpd]="wcHeaderData.tpd"
              [offWorkDays]="wcHeaderData.offWorkDays"
              [restrictedDutyDays]="wcHeaderData.restrictedDutyDays"
              [offWork]="wcHeaderData.offWork"
              [tempDisability]="wcHeaderData.tempDisability"
              [tempPermDisability]="wcHeaderData.tempPermDisability"
            />
          } @else {
            <app-time-tracking-header-ds
              [offWork]="data.offWork"
              [restricted]="data.restrictedDuty"
            />
          }
          <app-toggle-panel [export]="true" (exportEvent)="onExport($event)">
            @for (
              item of data.items;
              track item;
              let last = $last;
              let first = $first
            ) {
              @let desc =
                (item.workStatus | case: 'upper') +
                ' - ' +
                (item.substatus | case: 'sentence');

              <div class="tw-flex tw-gap-2 sm:tw-gap-4 tw-min-w-0">
                <div
                  class="tw-grow-0 tw-mb-2"
                  [class]="!last ? getLineClass(item) : ''"
                >
                  <div class=" tw-pb-2 tw-bg-[var(--sed-warm-white-color)]">
                    <app-icon
                      name="fa-light fa-calendar-check"
                      size="36px"
                      class="tw-cursor-pointer"
                      color="var(--sed-black-80-color)"
                      (click)="card.expanded = !card.expanded"
                    />
                  </div>
                </div>
                <div class="tw-grow tw-min-w-0">
                  <app-card
                    [expanded]="first"
                    #card
                    [showExpand]="true"
                    [padding]="false"
                    labelCase="none"
                    [label]="
                      item.substatus ? desc : (item.workStatus | case: 'upper')
                    "
                    theme="Light"
                    type="Medium"
                  >
                    <ng-template #header>
                      <div
                        class="tw-flex tw-flex-row-reverse tw-flex-wrap tw-items-center tw-grow tw-gap-2 md:tw-gap-8 tw-pr-2 md:tw-pr-4 max-md:tw-flex-col max-md:tw-items-stretch max-md:tw-w-full max-md:tw-basis-full"
                      >
                        <div
                          class="max-md:tw-flex max-md:tw-justify-between max-md:tw-w-full"
                        >
                          <b>Effective: </b>
                          {{ item.effectiveDate | format: 'd' }}
                        </div>
                        <div
                          class="max-md:tw-flex max-md:tw-justify-between max-md:tw-w-full"
                        >
                          <b>Date range: </b>
                          {{ item.beginDate | format: 'd' }} -
                          {{ item.endDate | format: 'd' }}
                        </div>
                        <app-tag [type]="getStatusTagType(item.statusCode)">
                          {{ item.status | case: 'sentence' }}
                        </app-tag>
                      </div>
                    </ng-template>
                    <div
                      class="tw-flex tw-flex-col lg:tw-flex-row tw-gap-4 sm:tw-gap-8 tw-p-2 sm:tw-p-8"
                    >
                      <div class="tw-w-full lg:tw-basis-1/3 tw-min-w-0">
                        <app-card label="details">
                          <app-detail-panel [dataSource]="item">
                            <app-detail-item label="type" name="type" />
                            <app-detail-item
                              label="duration"
                              name="duration"
                              format="n"
                            />
                            <app-detail-item
                              label="authorized off work"
                              name="authorizedOffWork"
                              format="b"
                            />
                          </app-detail-panel>
                        </app-card>
                      </div>
                      <div class="tw-w-full lg:tw-basis-1/3 tw-min-w-0">
                        <app-card label="dates">
                          <app-detail-panel [dataSource]="item">
                            <app-detail-item
                              label="Date last worked"
                              name="lastWorkedDate"
                              format="d"
                            />
                            <app-detail-item
                              label="Date disability"
                              name="disabilityDate"
                              format="d"
                            />
                            <app-detail-item
                              label="Date notice"
                              name="noticeDate"
                              format="d"
                            />
                            <app-detail-item
                              label="Date update"
                              name="updateDate"
                              format="d"
                            />
                          </app-detail-panel>
                        </app-card>
                      </div>
                      <div class="tw-w-full lg:tw-basis-1/3 tw-min-w-0">
                        <app-card label="anticipated rtw">
                          <!-- Desktop: 3-column grid -->
                          <div
                            class="tw-hidden xl:tw-grid tw-grid-cols-3 tw-gap-2 tw-pt-2"
                          >
                            <div></div>
                            <div class="tw-font-bold">Restricted</div>
                            <div class="tw-font-bold">Full</div>

                            <div class="tw-font-bold">Examiner</div>
                            <div
                              class="tw-flex tw-justify-between max-sm:tw-pl-0"
                            >
                              <span class="sm:tw-hidden tw-font-bold"
                                >Restricted</span
                              >
                              <span>{{
                                item.examinerRestrictedDate | format: 'd'
                              }}</span>
                            </div>
                            <div
                              class="tw-flex tw-justify-between max-sm:tw-pl-0"
                            >
                              <span class="sm:tw-hidden tw-font-bold"
                                >Full</span
                              >
                              <span>{{
                                item.examinerFullDate | format: 'd'
                              }}</span>
                            </div>

                            <div class="tw-font-bold">Case manager</div>
                            <div
                              class="tw-flex tw-justify-between max-sm:tw-pl-0"
                            >
                              <span class="sm:tw-hidden tw-font-bold"
                                >Restricted</span
                              >
                              <span>{{
                                item.caseManagerRestrictedDate | format: 'd'
                              }}</span>
                            </div>
                            <div
                              class="tw-flex tw-justify-between max-sm:tw-pl-0"
                            >
                              <span class="sm:tw-hidden tw-font-bold"
                                >Full</span
                              >
                              <span>{{
                                item.caseManagerFullDate | format: 'd'
                              }}</span>
                            </div>

                            <div class="tw-font-bold">Physician</div>
                            <div
                              class="tw-flex tw-justify-between max-sm:tw-pl-0"
                            >
                              <span class="sm:tw-hidden tw-font-bold"
                                >Restricted</span
                              >
                              <span>{{
                                item.physicianRestrictedDate | format: 'd'
                              }}</span>
                            </div>
                            <div
                              class="tw-flex tw-justify-between max-sm:tw-pl-0"
                            >
                              <span class="sm:tw-hidden tw-font-bold"
                                >Full</span
                              >
                              <span>{{
                                item.physicianFullDate | format: 'd'
                              }}</span>
                            </div>
                          </div>

                          <!-- Mobile: stacked layout -->
                          <div
                            class="tw-flex tw-flex-col tw-gap-4 tw-pt-2 xl:tw-hidden"
                          >
                            <div>
                              <div class="tw-font-bold tw-mb-1">Examiner</div>
                              <div
                                class="tw-grid tw-grid-cols-2 tw-gap-1 tw-pl-2"
                              >
                                <div class="tw-font-bold">Restricted</div>
                                <div>
                                  {{
                                    item.examinerRestrictedDate | format: 'd'
                                  }}
                                </div>
                                <div class="tw-font-bold">Full</div>
                                <div>
                                  {{ item.examinerFullDate | format: 'd' }}
                                </div>
                              </div>
                            </div>
                            <div>
                              <div class="tw-font-bold tw-mb-1">
                                Case manager
                              </div>
                              <div
                                class="tw-grid tw-grid-cols-2 tw-gap-1 tw-pl-2"
                              >
                                <div class="tw-font-bold">Restricted</div>
                                <div>
                                  {{
                                    item.caseManagerRestrictedDate | format: 'd'
                                  }}
                                </div>
                                <div class="tw-font-bold">Full</div>
                                <div>
                                  {{ item.caseManagerFullDate | format: 'd' }}
                                </div>
                              </div>
                            </div>
                            <div>
                              <div class="tw-font-bold tw-mb-1">Physician</div>
                              <div
                                class="tw-grid tw-grid-cols-2 tw-gap-1 tw-pl-2"
                              >
                                <div class="tw-font-bold">Restricted</div>
                                <div>
                                  {{
                                    item.physicianRestrictedDate | format: 'd'
                                  }}
                                </div>
                                <div class="tw-font-bold">Full</div>
                                <div>
                                  {{ item.physicianFullDate | format: 'd' }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </app-card>
                      </div>
                    </div>

                    <div
                      class="tw-flex tw-flex-col lg:tw-flex-row tw-gap-4 tw-p-2 sm:tw-p-8"
                    >
                      <div class="tw-w-full lg:tw-basis-1/3 tw-min-w-0">
                        <app-card label="modified duty">
                          <app-detail-panel [dataSource]="item">
                            <app-detail-item
                              label="employer notified of release to return on modified duty"
                              name="employerNotified"
                              format="d"
                            />
                            <app-detail-item
                              label="reason employee not on modified duty"
                              name="reasonNotModified"
                            />
                            <app-detail-item
                              label="available"
                              name="available"
                              format="b"
                            />
                            <app-detail-item
                              label="offered"
                              name="offered"
                              format="b"
                            />
                            <app-detail-item
                              label="accepted"
                              name="accepted"
                              format="b"
                            />
                            <app-detail-item
                              label="fullDays"
                              name="fullDays"
                              format="b"
                            />
                            <app-detail-item
                              label="occupation"
                              name="occupation"
                            />
                            <app-detail-item label="demand" name="demand" />
                          </app-detail-panel>
                        </app-card>
                      </div>
                    </div>
                    @if (item.isRestricted) {
                      <div class="tw-p-2 sm:tw-p-8">
                        <app-card label="work time">
                          <div
                            class="tw-w-full"
                            [class.table-scroll-region]="
                              (item.workTimes?.length ?? 0) > 5 &&
                              workTimePageSize !== 5
                            "
                          >
                            <app-table
                              [dataSource]="item.workTimes"
                              [globalFilterFields]="['dateBegin', 'dateEnd']"
                              [scrollable]="false"
                              recordLabel="schedule"
                              [pageSize]="workTimePageSize"
                              (pageSizeChange)="workTimePageSize = $event"
                              emptyIcon="fa-light fa-clock"
                            >
                              <app-column name="dateBegin" label="Days">
                                <ng-template let-data>
                                  {{ data.dateBegin | format: 'd' }} -
                                  {{ data.dateEnd | format: 'd' }}
                                </ng-template>
                              </app-column>
                              <app-column
                                name="sunday"
                                label="Sunday"
                                format="n"
                              />
                              <app-column
                                name="monday"
                                label="Monday"
                                format="n"
                              />
                              <app-column
                                name="tuesday"
                                label="Tuesday"
                                format="n"
                              />
                              <app-column
                                name="wednesday"
                                label="Wednesday"
                                format="n"
                              />
                              <app-column
                                name="thursday"
                                label="Thursday"
                                format="n"
                              />
                              <app-column
                                name="friday"
                                label="Friday"
                                format="n"
                              />
                              <app-column
                                name="saturday"
                                label="Saturday"
                                format="n"
                              />
                              <app-column name="total" label="total">
                                <ng-template let-data>
                                  {{ totalHours(data) | format: 'n' }}
                                </ng-template>
                              </app-column>
                            </app-table>
                          </div>
                        </app-card>
                      </div>
                    }
                  </app-card>
                  <div class="tw-pb-8"></div>
                </div>
              </div>
            }
          </app-toggle-panel>
        </ng-template>
      </app-tab>
      <app-tab label="Restrictions">
        <ng-template #content>
          <div class="tw-pt-4"></div>
          <div
            class="tw-w-full"
            [class.table-scroll-region]="
              (restrictions?.length ?? 0) > 5 && restrictionsPageSize !== 5
            "
          >
            <app-table
              [dataSource]="restrictions"
              [pageSize]="restrictionsPageSize"
              (pageSizeChange)="restrictionsPageSize = $event"
              [scrollable]="false"
              dataKey="id"
              recordLabel="Restriction"
              [stripedRows]="true"
            >
              <app-column name="restrictionBeginDate" label="Restriction Dates">
                <ng-template let-data>
                  {{ data.restrictionBeginDate | format: 'd' }} -
                  {{ data.restrictionEndDate | format: 'd' }}
                </ng-template>
              </app-column>
              <app-column name="createDate" label="Date created" format="d" />
              <app-column name="recommendBeginDate" label="Recommended Dates">
                <ng-template let-data>
                  {{ data.recommendBeginDate | format: 'd' }} -
                  {{ data.recommendEndDate | format: 'd' }}
                </ng-template>
              </app-column>
              <app-column name="type" label="type" />
              <app-column name="restriction" label="restriction" />
              <app-column name="category" label="category" />
            </app-table>
          </div>
        </ng-template>
      </app-tab>
    </app-tab-panel>
  `,
  styles: `
    .blue-line {
      background: linear-gradient(
          var(--sed-status-blue-color),
          var(--sed-status-blue-color)
        )
        no-repeat center/2px 100%;
    }
    .green-line {
      background: linear-gradient(
          var(--sed-status-green-color),
          var(--sed-status-green-color)
        )
        no-repeat center/2px 100%;
    }
    .yellow-line {
      background: linear-gradient(
          var(--sed-status-yellow-color),
          var(--sed-status-yellow-color)
        )
        no-repeat center/2px 100%;
    }
    .table-scroll-region {
      max-height: 320px;
      overflow-y: auto;
    }
  `,
})
export class TimeTrackingComponent extends BaseComponent implements OnInit {
  private exportService = inject(ExportService);
  private datePipe = inject(DatePipe);

  isWc = true;
  wcHeaderData: {
    ttd: number;
    tpd: number;
    offWorkDays: number;
    restrictedDutyDays: number;
    offWork: OffWorkData;
    tempDisability: TempDisabilityData;
    tempPermDisability: PermanentDisabilityData;
  } = {
    ttd: 0,
    tpd: 0,
    offWorkDays: 0,
    restrictedDutyDays: 0,
    offWork: {},
    tempDisability: {},
    tempPermDisability: {},
  };

  restrictions: any[] = [];
  data: any = {};
  workTimePageSize = 5;
  restrictionsPageSize = 5;

  ngOnInit(): void {
    this.isWc = this.view.currentClaim().lineOfBusiness === 'WC';
    this.view.hideLoading();
    this.loadTimeTracking();
  }
  async loadTimeTracking() {
    const response = await this.api.timeTracking.get({
      recordSource: this.view.currentClaim().recordSource,
      recordId: this.view.currentClaim().recordId,
    });
    this.wcHeaderData = {
      ttd: response.detail.lostDaysTTD,
      tpd: response.detail.lostDaysTPD,
      offWorkDays: response.detail.daysOffWork,
      restrictedDutyDays: response.detail.daysRestrictedDuty,
      offWork: {
        status: response.overview.status,
        employmentStatus: response.overview.employmentStatus,
        sameEmployer: response.overview.sameEmployer,
        salaryContinuance: response.overview.salaryContinuance,
        dateOfTermination: response.overview.dateOfTermination,
        disabilityEndReason: response.overview.disabilityEndReason,
      },
      tempDisability: {
        tempTotal: response.detail.temporaryTotal,
        tempPartial: response.detail.temporaryPartial,
      },
      tempPermDisability: {
        ppdScheduled: response.detail.ppdScheduled,
        ppdUnscheduled: response.detail.ppdUnscheduled,
        permanentTotal: response.detail.permanentTotal,
        death: response.detail.death,
      },
    };
    this.data = this.mapResponseToData(response as TimeTrackingResponse);
    this.restrictions = this.mapResponseToRestrictions(
      response as TimeTrackingResponse,
    );
  }

  private mapResponseToData(response: TimeTrackingResponse) {
    const restricted = 'restricted';
    const restrictedNTL = 'restrictedNTL';
    const restrictTWP = 'restrictTWP';

    const offWorkDays = response.detail?.daysOffWork ?? 0;
    const restrictedDutyDays = response.detail?.daysRestrictedDuty ?? 0;

    const items = (response.detail?.benefits ?? []).flatMap((benefit) => {
      const benefitDetails = benefit.benefitDetails ?? [];

      return benefitDetails.map((detail) => {
        const workStatus = detail.status;
        const workTimes = benefit.workTimes || [];
        let isRestricted = false;
        if (
          workStatus.toLowerCase() === restricted.toLowerCase() ||
          workStatus.toLowerCase().replaceAll(/[^A-z]/gi, '') ===
            restrictedNTL.toLowerCase() ||
          workStatus.toLowerCase().replaceAll(/[^A-z]/gi, '') ===
            restrictTWP.toLowerCase()
        ) {
          isRestricted = true;
        }

        const status =
          (detail.benefitStatus || '').charAt(0).toUpperCase() +
          (detail.benefitStatus || '').slice(1).toLowerCase();
        const substatus = detail.reason || null;

        return {
          isRestricted,
          status,
          statusCode: detail.benefitStatusCode,
          substatus,
          workStatus: detail.status,
          beginDate: detail.beginDate,
          endDate: detail.endDate,
          effectiveDate: benefit.effectiveDate,
          type: detail.type,
          duration: detail.duration ?? benefit.duration ?? 0,
          authorizedOffWork: detail.authorizedOffWork ?? false,
          lastWorkedDate: detail.dateLastWorked,
          disabilityDate: detail.dateDisability,
          noticeDate: detail.dateNotice,
          updateDate: detail.dateUpdate,
          examinerRestrictedDate: detail.exAnticipatedRestrictedRtrnDate,
          examinerFullDate: detail.exAnticipatedFullRtrnDate,
          caseManagerRestrictedDate:
            detail.caseMgrAnticipatedRestrictedRtrnDate,
          caseManagerFullDate: detail.caseMgrAnticipatedFullRtrnDate,
          physicianRestrictedDate: detail.physAnticipatedRestrictedRtrnDate,
          physicianFullDate: detail.physAnticipatedFullRtrnDate,
          employerNotified: benefit.returnToModifyDuty,
          reasonNotModified: benefit.noModifyDutyReason,
          available: detail.rtrnModifiedAvailable,
          offered: detail.rtrnModifiedOffered,
          accepted: detail.rtrnModifiedAccepted,
          fullDays: detail.rtrnFull,
          occupation: detail.rtrnModifiedOccupation,
          demand: detail.rtrnModifiedDemand,
          workTimes,
        };
      });
    });

    return {
      offWork: offWorkDays,
      restrictedDuty: restrictedDutyDays,
      items,
    };
  }

  private mapResponseToRestrictions(response: TimeTrackingResponse) {
    return response.overview.restrictionList.map((restriction, index) => {
      return {
        id: index + 1,
        restrictionBeginDate: restriction.beginRestrictionDate,
        restrictionEndDate: restriction.endRestrictionDate,
        createDate: restriction.createdDate,
        recommendBeginDate: restriction.beginRecomendedDate,
        recommendEndDate: restriction.endRecomendedDate,
        type: restriction.type,
        restriction: restriction.restriction,
        category: restriction.category,
      };
    });
  }

  totalHours(item: any) {
    return (
      item.sunday +
      item.monday +
      item.tuesday +
      item.wednesday +
      item.thursday +
      item.friday +
      item.saturday
    );
  }

  getStatusTagType(statusCode: string): TagType {
    if (statusCode === 'OA') {
      return 'Success';
    } else if (statusCode === 'OD') {
      return 'Warn';
    } else if (
      statusCode === 'OP' ||
      statusCode === 'PA' ||
      statusCode === 'WP' ||
      statusCode === 'WR'
    ) {
      return 'Info';
    } else {
      return 'Grey';
    }
  }

  getLineClass(item: any) {
    if (item.status === 'open approved') {
      return 'green-line';
    } else if (item.status === 'denied') {
      return 'yellow-line';
    } else {
      return 'blue-line';
    }
  }

  async onExport(format: ExportFormat) {
    try {
      let columns = [
        { propertyName: 'workStatus', headerText: 'Status', case: 'upper' },
        {
          propertyName: 'effectiveDate',
          headerText: 'Effective Date',
          format: 'd',
        },
        { propertyName: 'duration', headerText: 'Duration' },
        { propertyName: 'beginDate', headerText: 'Begin Date', format: 'd' },
        { propertyName: 'endDate', headerText: 'End Date', format: 'd' },
        { propertyName: 'type', headerText: 'Type' },
        { propertyName: 'status', headerText: 'Benefit Status' },
        { propertyName: 'substatus', headerText: 'Reason' },
      ];
      const excludedProperties = new Set();
      if (!(this as any).showTitle) excludedProperties.add('title');
      columns = columns.filter(
        (col) => !excludedProperties.has(col.propertyName),
      );
      const rows = this.data.items.map((item: any) => ({
        ...item,
        workStatus: item.substatus
          ? `${item.workStatus} - ${item.substatus}`
          : item.workStatus,
      }));
      const claimNumber = this.view.currentClaim().fileNumber;
      const formattedDate = this.datePipe.transform(new Date(), 'yyyyMMddHHmm');
      const baseName = `${claimNumber}-TimeTracking-${formattedDate}`;
      const filename = `${baseName}.${format}`;
      if (format === 'csv') {
        const csvData = ClaimExportMapper.toCsvExport(rows, columns, baseName);
        await this.exportService.exportCsv(csvData, filename);
      } else if (format === 'xlsx') {
        const excelData = ClaimExportMapper.toExcelExport(
          rows,
          columns,
          baseName,
          baseName,
        );
        await this.exportService.exportExcel(excelData, filename);
      }
    } catch (err) {
      if ((this as any).handleError) {
        (this as any).handleError(err);
      } else {
        (this as any).error = err;
      }
    }
  }
}
